/**
 * Funnel-conversion chart for the admin dashboard.
 * Shows Lead → Approved → Signed → Live with per-stage counts and
 * conversion rates (both from the previous stage and from lead).
 */
import { useEffect, useState } from "react";
import { Loader2, ArrowRight } from "lucide-react";
import { api } from "@/lib/auth";

const STAGE_COLOURS = {
    lead: "bg-white/10 text-white",
    approved: "bg-sky-400/10 text-sky-300 border-sky-400/40",
    signed: "bg-[#FF5A00]/15 text-[#FF5A00] border-[#FF5A00]/60",
    live: "bg-emerald-400/15 text-emerald-300 border-emerald-400/40",
};

const pct = (v) => v == null ? "—" : `${Math.round(v * 100)}%`;

export default function FunnelSnapshot() {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [err, setErr] = useState(null);

    useEffect(() => {
        (async () => {
            try {
                const { data } = await api.get("/reports/funnel");
                setData(data);
            } catch (e) {
                if (e?.response?.status === 403) return;
                setErr(e?.response?.data?.detail || "Could not load funnel.");
            } finally { setLoading(false); }
        })();
    }, []);

    if (loading) {
        return (
            <div className="mt-6 border border-white/10 bg-[#1B2E55]/50 p-6 flex items-center gap-2 text-xs text-slate-400"
                data-testid="dashboard-funnel-loading">
                <Loader2 size={12} className="animate-spin" /> Loading funnel…
            </div>
        );
    }
    if (err || !data) return null;
    const stages = data.stages || [];
    const maxCount = Math.max(...stages.map((s) => s.count || 0), 1);

    return (
        <div className="mt-6 border border-white/10 bg-[#1B2E55]/50 p-6" data-testid="dashboard-funnel-card">
            <div className="flex items-center justify-between mb-5">
                <div className="font-mono-tech text-[10px] uppercase tracking-[0.3em] text-[#FF5A00]">/ Funnel · lead → live</div>
                <div className="text-[10px] font-mono-tech uppercase tracking-widest text-slate-500">
                    Lifetime
                </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-4 gap-3" data-testid="dashboard-funnel-stages">
                {stages.map((s, i) => (
                    <div key={s.key} className={`border ${(STAGE_COLOURS[s.key] || STAGE_COLOURS.lead)} p-4`}
                        data-testid={`dashboard-funnel-stage-${s.key}`}>
                        <div className="flex items-center justify-between">
                            <div className="text-[10px] font-mono-tech uppercase tracking-[0.3em] opacity-80">/ {s.label}</div>
                            {i > 0 && (
                                <div className="text-[10px] font-mono-tech uppercase tracking-widest opacity-70">
                                    {pct(s.rate_from_previous)}
                                </div>
                            )}
                        </div>
                        <div className="mt-2 font-display font-black tracking-tighter text-3xl">{s.count}</div>
                        {i > 0 && (
                            <div className="mt-2 h-1 bg-white/10 overflow-hidden">
                                <div className="h-full bg-current opacity-70"
                                    style={{ width: `${Math.max(4, (s.count / maxCount) * 100)}%` }} />
                            </div>
                        )}
                        {i > 0 && (
                            <div className="mt-2 text-[10px] font-mono-tech uppercase tracking-widest opacity-70">
                                {pct(s.rate_from_lead)} of leads
                            </div>
                        )}
                    </div>
                ))}
            </div>

            <div className="mt-4 hidden sm:flex items-center justify-center gap-2 text-slate-500">
                <ArrowRight size={12} />
                <span className="text-[10px] font-mono-tech uppercase tracking-widest">
                    Lead {pct(stages[0]?.rate_from_lead)} → Live {pct(stages[stages.length - 1]?.rate_from_lead)}
                </span>
            </div>
        </div>
    );
}
